import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    TrendingUp, ShieldCheck, Users, Lock, Globe, Clock, Trophy, DollarSign,
    Image as ImageIcon, PlayCircle, ArrowRight, Scale, Cpu, Eye, Store,
    ShoppingBag, BadgeCheck, CreditCard, Sparkles,
} from "lucide-react";

const img = (id, w = 800) => `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;
const HERO_IMG = "1586528116311-ad8dd3c8310d";

const WHY = [
    { icon: TrendingUp, title: "True Market Value", text: "Sealed bids reveal what buyers are really willing to pay — no inflated prices or artificial bidding." },
    { icon: Scale, title: "Fair & Transparent", text: "No hidden reserves. No bidding wars. No last-second sniping. Just fair competition." },
    { icon: Users, title: "Serious Buyers", text: "Buyers submit their highest single bid, attracting motivated buyers ready to buy." },
    { icon: Lock, title: "Secure & Trusted", text: "Advanced verification and secure payments powered by Stripe keep you protected." },
    { icon: Globe, title: "Nationwide Reach", text: "Your items are seen by thousands of qualified buyers across the country." },
    { icon: Clock, title: "Save Time & Grow", text: "Easy listings, a streamlined process, and fast payouts so you can focus on your business." },
];

const STEPS = [
    { icon: ImageIcon, title: "List Your Item", text: "Add photos, details, and set your starting price." },
    { icon: Lock, title: "Buyers Submit Bids", text: "Buyers place their highest confidential bid — no one sees competing offers." },
    { icon: Clock, title: "Auction Closes", text: "No sniping. No bidding wars. No manipulation." },
    { icon: Trophy, title: "Winner Determined", text: "The highest qualified bid wins. It's that simple." },
    { icon: DollarSign, title: "Get Paid Securely", text: "Payments and payouts are processed securely through Stripe." },
];

const BUSINESS_TYPES = [
    { title: "Retailers", text: "Move overstock and closeouts faster.", id: "1553413077-190dd305871c" },
    { title: "Manufacturers", text: "Turn surplus inventory into revenue.", id: "1567521464027-f127ff144326" },
    { title: "Liquidators", text: "Reach more buyers and maximize returns.", id: "1565793298595-6a879b1d9492" },
    { title: "Estate Companies", text: "Provide a fair, transparent process for clients.", id: "1564013799919-ab600027ffc6" },
    { title: "Small Businesses", text: "Grow your business with a wider audience.", id: "1560472354-b33ff0c44a43" },
    { title: "And More", text: "Nearly anything can be sold on OmniAuc.", id: "1586023492125-27b2c045efd7" },
];

const TRUST = [
    { icon: ShoppingBag, label: "Fair for Buyers" },
    { icon: Store, label: "Fair for Sellers" },
    { icon: ShieldCheck, label: "Built on Trust" },
    { icon: Cpu, label: "Powered by Technology" },
    { icon: Eye, label: "Driven by Transparency" },
];

const RECENT = [
    { name: "MacBook Pro 16\" M2", state: "Ended · Sold", amount: "$1,250", active: false },
    { name: "DeWalt 20V Max Drill Set", state: "Ended · Sold", amount: "$410", active: false },
    { name: "Canon EOS R6 Camera", state: "Ended · Sold", amount: "$980", active: false },
    { name: "Industrial Tool Chest", state: "Active · Ends in 2h", amount: "$320", active: true },
];

function scrollToSteps() {
    const el = document.getElementById("how-omniauc-works");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ---------------- Decorative seller dashboard mockup ----------------
function DashboardMockup() {
    const stats = [
        { label: "Total Auctions", value: "128" },
        { label: "Items Sold", value: "$48,650" },
        { label: "Active Bids", value: "762" },
        { label: "Success Rate", value: "89%" },
    ];
    return (
        <div className="rounded-2xl border border-border bg-card/80 p-4 shadow-2xl backdrop-blur sm:p-5" aria-hidden="true">
            <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="flex items-center gap-2 font-display text-sm font-semibold">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brass text-brass-foreground"><Sparkles className="h-3.5 w-3.5" /></span>
                    OmniAuc
                </span>
                <span className="text-xs text-muted-foreground">Seller Dashboard</span>
            </div>

            <p className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground">Overview</p>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {stats.map((s) => (
                    <div key={s.label} className="rounded-lg border border-border bg-secondary/30 p-2.5">
                        <p className="text-[10px] text-muted-foreground">{s.label}</p>
                        <p className="font-display text-base font-bold tabular-nums">{s.value}</p>
                    </div>
                ))}
            </div>

            <p className="mt-4 text-[11px] uppercase tracking-wide text-muted-foreground">Recent Auctions</p>
            <div className="mt-2 space-y-1.5">
                {RECENT.map((r) => (
                    <div key={r.name} className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2">
                        <div className="min-w-0">
                            <p className="truncate text-xs font-medium">{r.name}</p>
                            <p className={`text-[10px] ${r.active ? "text-electric" : "text-muted-foreground"}`}>{r.state}</p>
                        </div>
                        <span className="ml-3 shrink-0 font-display text-sm font-semibold tabular-nums">{r.amount}</span>
                    </div>
                ))}
            </div>

            <div className="mt-3 flex items-start gap-2 rounded-lg border border-brass/30 bg-brass/10 p-3">
                <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
                <div>
                    <p className="text-xs font-semibold text-brass">True Market Value. Better Results.</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">Our sealed-bid system reveals what buyers really want to pay — so you get the best value for your items.</p>
                </div>
            </div>
        </div>
    );
}

export default function HowItWorks() {
    return (
        <div data-testid="how-it-works-page">
            {/* ---------------- HERO ---------------- */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_15%_10%,rgba(33,140,255,0.16)_0%,rgba(33,140,255,0)_60%)]" />
                <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-20">
                    <div>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-brass/30 bg-brass/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brass">
                            <Store className="h-3.5 w-3.5" /> For Businesses
                        </span>
                        <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
                            The Fair Auction Marketplace<sup className="text-xl">™</sup> for{" "}
                            <span className="text-electric">Modern Businesses</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                            OmniAuc is built to help businesses sell with confidence. No hidden reserves. No bidding wars.
                            No sniping. Just true market value and real results.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Button asChild size="lg" className="rounded-[10px]" data-testid="hiw-start-selling">
                                <Link to="/signup"><Store className="mr-2 h-4 w-4" /> Start selling today</Link>
                            </Button>
                            <Button onClick={scrollToSteps} size="lg" variant="outline" className="rounded-[10px]" data-testid="hiw-see-how">
                                <PlayCircle className="mr-2 h-4 w-4 text-electric" /> See how it works
                            </Button>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm">
                            <span className="inline-flex items-center gap-2 text-muted-foreground"><BadgeCheck className="h-4 w-4 text-success" /> Verified Users</span>
                            <span className="inline-flex items-center gap-2 text-muted-foreground"><CreditCard className="h-4 w-4 text-electric" /> Secure Payments by Stripe</span>
                            <span className="inline-flex items-center gap-2 text-muted-foreground"><ShieldCheck className="h-4 w-4 text-brass" /> Fair. Transparent. Built for Everyone.</span>
                        </div>
                    </div>

                    {/* Right: hero image + dashboard mockup */}
                    <div className="relative">
                        <div className="overflow-hidden rounded-2xl border border-border">
                            <img src={img(HERO_IMG, 1000)} alt="Business owner preparing an order for shipment" className="h-56 w-full object-cover sm:h-72 lg:h-80" loading="eager" />
                        </div>
                        <div className="relative z-10 mt-[-3rem] ml-auto w-[92%] sm:w-[85%]">
                            <DashboardMockup />
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- WHY BUSINESSES CHOOSE (white) ---------------- */}
            <section className="bg-white">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] sm:text-4xl">
                            Why <span className="text-amber-600">Businesses</span> Choose OmniAuc
                        </h2>
                        <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-amber-500" />
                    </div>
                    <div className="mt-14 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                        {WHY.map(({ icon: Icon, title, text }) => (
                            <div key={title} className="text-center" data-testid="hiw-why-card">
                                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0A1226]/[0.05] text-[#16264a]">
                                    <Icon className="h-6 w-6" />
                                </span>
                                <h3 className="mt-4 font-display text-lg font-bold text-[#1A1A1A]">{title}</h3>
                                <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-600">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- HOW OMNIAUC WORKS (dark) ---------------- */}
            <section id="how-it-works-section" className="border-y border-border bg-[hsl(221_59%_7%)]">
                <div id="how-omniauc-works" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
                    <h2 className="text-center font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        How <span className="text-brass">OmniAuc</span> Works
                    </h2>
                    <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-brass" />

                    <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
                        {STEPS.map(({ icon: Icon, title, text }, i) => (
                            <div key={title} className="relative text-center" data-testid="hiw-step">
                                <div className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-border bg-card">
                                    <Icon className="h-7 w-7 text-electric" />
                                    <span className="absolute -top-2 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-brass bg-background font-display text-xs font-bold text-brass">
                                        {i + 1}
                                    </span>
                                </div>
                                {i < STEPS.length - 1 && (
                                    <ArrowRight className="absolute right-[-18px] top-9 hidden h-5 w-5 text-border lg:block" />
                                )}
                                <h3 className="mt-5 font-display text-base font-bold">{title}</h3>
                                <p className="mx-auto mt-2 max-w-[12rem] text-sm leading-relaxed text-muted-foreground">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- PERFECT FOR EVERY BUSINESS (white) ---------------- */}
            <section className="bg-white">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] sm:text-4xl">
                            Perfect for Every Type of Business
                        </h2>
                        <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-amber-500" />
                    </div>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {BUSINESS_TYPES.map(({ title, text, id }) => (
                            <div key={title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(16,24,40,0.06)]" data-testid="hiw-business-card">
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={img(id, 600)} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]" />
                                </div>
                                <div className="p-5">
                                    <h3 className="font-display text-lg font-bold text-[#1A1A1A]">{title}</h3>
                                    <p className="mt-1 text-sm text-slate-600">{text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- CTA BANNER ---------------- */}
            <section className="bg-gradient-to-r from-brass to-[hsl(38_92%_46%)]">
                <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-4 py-14 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="text-brass-foreground">
                        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Ready to Unlock True Market Value?</h2>
                        <p className="mt-2 max-w-xl text-base text-brass-foreground/80">
                            Join thousands of businesses already selling smarter on OmniAuc.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Button asChild size="lg" className="rounded-[10px] bg-[hsl(221_59%_9%)] text-white hover:bg-[hsl(221_59%_14%)]" data-testid="hiw-create-seller">
                            <Link to="/signup"><Store className="mr-2 h-4 w-4" /> Create seller account</Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" className="rounded-[10px] border-brass-foreground/30 bg-transparent text-brass-foreground hover:bg-brass-foreground/10" data-testid="hiw-list-auction">
                            <Link to="/dashboard/seller"><ArrowRight className="mr-2 h-4 w-4" /> List your first auction</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* ---------------- TRUST STRIP ---------------- */}
            <section className="bg-[hsl(221_59%_7%)]">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-4 px-4 py-8 sm:px-6">
                    {TRUST.map(({ icon: Icon, label }) => (
                        <span key={label} className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon className="h-4 w-4 text-electric" /> {label}
                        </span>
                    ))}
                </div>
            </section>
        </div>
    );
}
